/**
 * Componente especifico generador a partride de ADF 5.0.0.
 */

import { SearchQueryBuilderService, SearchWidget } from '@alfresco/adf-content-services';
import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { CustomSearchWidgetSettings } from 'projects/alden-extension/src/lib/models/custom-viewer-config';
import { BehaviorSubject, Subject } from 'rxjs';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { executeFunction } from 'projects/alden-extension/src/lib/utils/dynamic-functions';
import { NULL, OPERATORS, OPERATORS_LOAD } from 'projects/alden-extension/src/lib/models/alden-constants';

@Component({
    selector: 'smt-search-text',
    templateUrl: './search-text.component.html',
    styleUrls: ['./search-text.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'smt-search-text' }
})
export class SMTSearchTextComponent implements SearchWidget, OnInit {

    /** The content of the text box. */
    value = '';

    id: string;
    settings: CustomSearchWidgetSettings;
    context: SearchQueryBuilderService;
    startValue: string;
    operator: string = 'OR';
    displayValue$: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);
    displayError$: Subject<string> = new Subject<string>();
    
    //Custom SMT
    query: string;
    editable: boolean;

    //Multiple
    visible = true;
    chipOperator: string = 'OR';
    removable = true;
    terms: string[] = [];
    excludedTerms: string[] = [];
    addOnBlur = true;
    readonly separatorKeysCodes = [ENTER, COMMA] as const;
    isChecked: boolean; 
    useChips: boolean;
    allowOperatorChange: boolean;

    filterIsNull: boolean = false;
    nullable: boolean;
    filterNullApplied: boolean = false;

    constructor() {
        this.editable = true;
        this.useChips = true;
        this.allowOperatorChange = true;
        this.nullable = true;
    }

    ngOnInit() {
        if(this.settings && this.settings.allowUpdateOnChange !== undefined && this.settings.allowUpdateOnChange !== null) {
            this.editable = this.settings.allowUpdateOnChange;
        }
        
        if(this.settings && this.settings.useChips !== undefined && this.settings.useChips !== null) {
            this.useChips = this.settings.useChips;
        }

        if(this.settings && this.settings.allowOperatorChange !== undefined && this.settings.allowOperatorChange !== null) {
            this.allowOperatorChange = this.settings.allowOperatorChange;
        }

        this.operator = (this.settings.operator) ? this.settings.operator: this.operator;
        if (this.context && this.settings && this.settings.pattern) {
            const pattern = new RegExp(this.settings.pattern, 'g');
            const match = pattern.exec(this.context.queryFragments[this.id] || '');

            if (match && match.length > 1) {
                this.value = match[1];
            }

            if (this.startValue) {
                this.setValue(this.startValue);
            }
        }

        this.checkIfNullable();
    }

    checkIfNullable(){
        if(this.settings?.nullable !== undefined && this.settings?.nullable !== null) {
            this.nullable =  this.settings.nullable;
        }
    }

    hasValidValue() {
        return !!this.value || this.terms.length > 0 || this.excludedTerms.length > 0 || this.filterIsNull || this.filterNullApplied;
    }

    getCurrentValue() {
        return this.value;
    }

    getAppliedValue(): any {
        if(!this.useChips)
            return this.value;
        if(this.filterIsNull)
            return NULL;

        const val = [OPERATORS_LOAD[this.chipOperator]];
        val.push(...this.terms);
        if(this.excludedTerms)
           val.push(...this.excludedTerms.map(term => "!"+term));

        return val;
    }

    submitValues() {
        this.updateQuery();
        this.updateDisplayValue();
    }

    reset() {
        this.value = '';
        this.terms = [];
        this.filterIsNull = false;
        this.excludedTerms = [];
        this.updateQuery();
        this.updateDisplayValue();
    }

    updateDisplayValue(): void {
        if(this.filterIsNull){
            this.displayValue$.next({
                value: 'Sin valor',
                tooltip: 'Sin valor'
            });
        } else if (!this.value && this.terms.length === 0 && this.excludedTerms.length === 0) {
            this.displayValue$.next(null);
        } else {
            if(this.terms.length+this.excludedTerms.length > 1) {
                this.displayValue$.next({
                    value: `${this.terms.length+this.excludedTerms.length} seleccionados`,
                    tooltip: this.terms.join(", ")
                });
            } else {
                const maxLength = 25;
                var value;
                if(this.terms.length > 0){
                    value = this.terms[0];
                }else if(this.excludedTerms.length > 0){
                    value = this.excludedTerms[0];
                }else{
                    value = this.value;
                }
                //const value = this.terms.length > 0 ? this.terms[0] : this.excludedTerms[0];
                this.displayValue$.next({
                        value: value.substring(0, maxLength) + (value.length > maxLength ? '...' : ''),
                        tooltip: value
                });
            }
        }
    }

    updateQuery() {
        if (this.context && this.settings) {
            this.query = '';
            const prefixOperator = this.settings.prefixOperator ? this.settings.prefixOperator : "=";
            this.filterNullApplied = this.filterIsNull;
            if(this.filterIsNull || this.value === "NULL"){
                ;
                let fieldsArray: string[] = this.settings.field ? [`(ISNULL:'${this.settings.field}' OR ISUNSET:'${this.settings.field}' OR NOT @${this.settings.field}:[* TO *])`] : [];
                this.settings.extraFields?.forEach(field => {
                    fieldsArray.push(`(ISNULL:'${field}' OR ISUNSET:'${field}' OR NOT @${field}:[* TO *])`);
                });
                this.query = fieldsArray.length > 0 ? '('+fieldsArray.join(` ${this.operator} `)+')' : '';
            }else if(this.useChips && (this.value || this.terms.length > 0 || this.excludedTerms.length > 0)){
                if(this.value){
                    this.add();
                }

                let fieldQuery = this.settings.field ? this.terms.map(term => `${prefixOperator}${this.settings.field}:'${this.getSearchPrefix()}${this.formatValue(term)}${this.getSearchSuffix()}'`).join(" "+this.chipOperator+" ") : "";
                if(this.excludedTerms.length > 0 && this.settings.field){
                    fieldQuery += fieldQuery ? " "+this.chipOperator+" " : "";
                    fieldQuery += this.excludedTerms.map(term => `-@${this.settings.field}:'${this.getSearchPrefix()}${this.formatValue(term)}${this.getSearchSuffix()}'`).join(" "+this.chipOperator+" ")
                }

                let fieldsArray: string[] = fieldQuery ? [fieldQuery] : [];

                this.settings.extraFields?.forEach(field => {
                    let fieldQuery = this.terms.map(term => `${prefixOperator}${field}:'${this.getSearchPrefix()}${this.formatValue(term)}${this.getSearchSuffix()}'`).join(" "+this.chipOperator+" ");
                    if(this.excludedTerms.length > 0) fieldQuery +=" "+ this.chipOperator+" "+ this.excludedTerms.map(term => `-@${field}:'${this.getSearchPrefix()}${this.formatValue(term)}${this.getSearchSuffix()}'`).join(" "+this.chipOperator+" ");
                    fieldsArray.push(fieldQuery);
                });

                this.query = fieldsArray.length > 0 ? '('+fieldsArray.join(` ${this.operator} `)+')' : '';
            }else{
                if(this.value){
                    //Recorrer y añadir a query
                    let fieldsArray: string[] = this.settings.field ? [`${prefixOperator}${this.settings.field}:'${this.getSearchPrefix()}${this.formatValue(this.value)}${this.getSearchSuffix()}'`] : [];
                    this.settings.extraFields?.forEach(field => {
                        fieldsArray.push(`+${field}:'${this.getSearchPrefix()}${this.formatValue(this.value)}${this.getSearchSuffix()}'`);
                    });
                    this.query = fieldsArray.length > 0 ? '('+fieldsArray.join(` ${this.operator} `)+')' : '';
                }
                
            }
            this.context.queryFragments[this.id] = this.query;
            this.context.update();
        }
    }

    private formatValue(value: string): string {
        const formatFunction = this.settings.formatFunction;
        if (formatFunction) {
            return executeFunction(formatFunction, value);
        } else {
            return value;
        }
    }

    //Custom SMT
    getCurrentQuery() {
        return this.query;
    }

    setValue(value: string) {
        //  Si viene de defaultValue y es un array
        this.terms = [];
        this.excludedTerms = [];
        if(value === NULL) {
            this.filterIsNull = true;
        } else if(value && Array.isArray(value))  {
            if(OPERATORS[value[0]]){
                this.chipOperator = OPERATORS[value[0]];
                value.shift();
            }
            value.forEach(v => {  
                this.value = v;
                if(this.value.startsWith("!")){
                    this.value = this.value.slice(1);
                    this.exclude();
                }
                else this.add();
            });
        } else {
           this.value = value;
        }
    }

    getSearchPrefix(): string {
        return this.settings.searchPrefix ? this.settings.searchPrefix : '';
    }

    getSearchSuffix(): string {
        return this.settings.searchSuffix ? this.settings.searchSuffix : '';
    }

    disable() {
        this.editable = false;
    }

    enable() {
        this.editable = true;
    }
     
    add(): void {
        if (this.value && !this.terms.includes(this.value)) {
            this.terms.push(this.value);
            this.value = "";
        }
    }

    exclude(): void {
        if (this.value && !this.excludedTerms.includes(this.value)) {
            this.excludedTerms.push(this.value);
            this.value = "";
        }
    }

    remove(term: string): void {
        const index = this.terms.indexOf(term);
        if (index >= 0) {
            this.terms.splice(index, 1);
        }else{
            const index = this.excludedTerms.indexOf(term);
            if (index >= 0) {
                this.excludedTerms.splice(index, 1);
            }
        }
    }

}